
import Vue from 'vue';
import { component } from '@/helpers/vueTyped';
import WithFetch from '@/components/Helpers/WithFetch.vue';
import ContentCardGhostLoader from './Card/ContentCard/ContentCardGhostLoader.vue';
import ScoreCardGhostLoader from './Card/ScoreCard/ScoreCardGhostLoader.vue';
import type { Summary as ContentSummary } from '@/api/validators/content';
import type { Summary as ScoreSummary, SummaryType } from '@/components/Sport/types';
import type { MergedApi } from '@/api/server';
import type { TeamScoreCardProps } from '@/components/SharedViews/Card/ScoreCard/TeamScoreCard.vue';
import type {
  MotorsportSummary,
  GolfStrokeSummary,
  TennisSummary,
  GolfMatchPlaySummary,
} from '@/api/converters/score';

interface SummaryFetcherProps {
  type: SummaryType;
  summaryId: string;
  autoUpdate?: boolean;
  itemSubType?: string | null;
  isCached?: boolean;
  cardFocusedCallback: (event: FocusEvent, summary: ContentSummary) => void;
  lazyLoad?: boolean;
}

interface SummaryFetcherEvents {
  onLoad: ContentSummary;
  onRender: void;
}

export default component<SummaryFetcherProps, SummaryFetcherEvents>()({
  name: 'SummaryFetcher',

  props: {
    type: {},
    summaryId: {},
    autoUpdate: Boolean,
    itemSubType: {},
    isCached: {
      type: Boolean,
      default: true,
    },
    cardFocusedCallback: {},
    lazyLoad: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      isVisible: false,
      elementEventHandlerMap: new Map<HTMLElement, () => void>(),
      throttle: false,
    };
  },

  computed: {
    apiCall(): keyof MergedApi {
      if (this.type === 'articles' || this.type === 'video') {
        return 'fetchContentSummary';
      }

      return 'fetchScoreSummary';
    },
  },

  methods: {
    focus() {
      if (this.$refs.lazyloading) {
        (this.$refs.lazyloading as HTMLElement).focus();
      } else if (this.$refs.loading) {
        (this.$refs.loading as HTMLElement).focus();
      } else if (this.$refs.card) {
        ((this.$refs.card as Vue).$el as HTMLElement).focus();
      } else {
        this.$spatialFocus.lastFocused?.focus();
      }
    },

    async focusNextTick() {
      await this.$nextTick();
      this.focus();
    },

    cardFocus(summary: ContentSummary) {
      return (event: FocusEvent) => {
        this.cardFocusedCallback(event, summary);
      };
    },

    calcIsVisible() {
      const lazyCard = this.$refs.lazyloading as HTMLElement;

      const bodyRect = document.body.getBoundingClientRect();
      const lazyCardRect = lazyCard.getBoundingClientRect();

      this.isVisible = lazyCardRect.x < (bodyRect.right + lazyCardRect.width) && lazyCardRect.y < bodyRect.bottom;
    },

    parentScrollHandler() {
      if (this.throttle) { return; }


      this.throttle = true;
      setTimeout(() => this.throttle = false, 250);

      this.calcIsVisible();

      if (this.isVisible) {
        const lazyCard = this.$refs.lazyloading as HTMLElement;
        let parent = lazyCard.parentElement as HTMLElement | null | undefined;

        while (parent != null && parent !== document.body) {
          parent.removeEventListener('scroll', this.parentScrollHandler);

          parent = parent.parentElement;
        }
      }
    },
  },

  mounted() {
    if (this.lazyLoad) {

      this.calcIsVisible();

      if (!this.isVisible) {
        const lazyCard = this.$refs.lazyloading as HTMLElement;
        let parent = lazyCard.parentElement as HTMLElement | null | undefined;

        while (parent != null && parent !== document.body) {
          parent.addEventListener('scroll', this.parentScrollHandler, { passive: true });

          parent = parent.parentElement;
        }
      }
    }
  },

  destroyed() {
    const lazyCard = this.$refs.lazyloading as HTMLElement;

    if (lazyCard) {
      let parent = lazyCard.parentElement as HTMLElement | null | undefined;

      while (parent != null && parent !== document.body) {
        parent.removeEventListener('scroll', this.parentScrollHandler);

        parent = parent.parentElement;
      }
    }
  },

  render() {
    if (this.lazyLoad && !this.isVisible) {
      return (
        this.type === 'articles' || this.type === 'video' ? (
          <ContentCardGhostLoader
            ref='lazyloading'
            v-spatial-element
          />
        ) : (
          <ScoreCardGhostLoader ref='lazyloading' v-spatial-element />
        )
      );
    }

    if (document.activeElement === this.$refs.lazyloading) {
      this.focusNextTick();
    }

    return (
      <WithFetch
        apiCall={this.apiCall}
        payload={[this.type, this.summaryId, this.itemSubType]}
        nativeOnFocus={this.focus}
        onLoad={(summary: ContentSummary) => {
          this.$emit('load', summary);
        }}
        autoUpdate={this.autoUpdate}
        isCached={this.isCached}
        scopedSlots={{
          loaded: ({
            response,
          }: {
            response: ContentSummary | ScoreSummary;
          }) => {
            this.$emit('render', response);

            if (document.activeElement === this.$refs.loading) {
              this.focusNextTick();
            }

            if (this.type === 'articles' || this.type === 'video') {
              return (
                <ContentCard
                  ref='card'
                  nativeOnFocus={this.cardFocus(response as ContentSummary)}
                  summary={response as ContentSummary}
                  type={this.type}
                />
              );
            }

            if (
              this.type === 'football' ||
              this.type === 'rugby' ||
              this.type === 'cricket'
            ) {
              return (
                <TeamScoreCard
                  ref='card'
                  nativeOnFocus={this.cardFocusedCallback}
                  {...{props: { summary: response, sport: this.type} as TeamScoreCardProps}}
                />
              );
            }

            if (this.type === 'tennis') {
              return (
                <TennisScoreCard
                  ref='card'
                  nativeOnFocus={this.cardFocusedCallback}
                  summary={response as TennisSummary}
                />
              );
            }

            if (this.type === 'motorsport') {
              return (
                <MotorsportScoreCard
                  ref='card'
                  nativeOnFocus={this.cardFocusedCallback}
                  summary={response as MotorsportSummary}
                />
              );
            }

            if (this.type === 'golf' && this.itemSubType != null) {
              return this.itemSubType.includes('stroke') ? (
                <GolfStrokeScoreCard
                  ref='card'
                  nativeOnFocus={this.cardFocusedCallback}
                  summary={response as GolfStrokeSummary}
                />
              ) : (
                <GolfMatchPlayScoreCard
                  ref='card'
                  summary={response as GolfMatchPlaySummary}
                  sport={'golf'}
                />
              );
            }

            return null;
          },
        }}
      >
        <template slot='loading'>
          {this.type === 'articles' || this.type === 'video' ? (
            <ContentCardGhostLoader
              ref='loading'
              v-spatial-element
            />
          ) : (
            <ScoreCardGhostLoader ref='loading' v-spatial-element />
          )}
        </template>
      </WithFetch>
    );
  },
});

const ContentCard = Vue.component<any>(
  'ContentCard',
  () => import(/* webpackChunkName: "ContentCard" */ '@/components/SharedViews/Card/ContentCard/ContentCard.vue'),
) as typeof import('@/components/SharedViews/Card/ContentCard/ContentCard.vue').default;

const TeamScoreCard = Vue.component<any>(
  'TeamScoreCard',
  () => import(/* webpackChunkName: "TeamScoreCard" */ '@/components/SharedViews/Card/ScoreCard/TeamScoreCard.vue'),
) as typeof import('@/components/SharedViews/Card/ScoreCard/TeamScoreCard.vue').default;

const MotorsportScoreCard = Vue.component<any>(
  'MotorsportScoreCard',
  () => import(/* webpackChunkName: "MotorsportScoreCard" */ '@/components/SharedViews/Card/ScoreCard/MotorsportScoreCard/MotorsportScoreCard.vue'),
) as typeof import('@/components/SharedViews/Card/ScoreCard/MotorsportScoreCard/MotorsportScoreCard.vue').default;

const GolfStrokeScoreCard = Vue.component<any>(
  'GolfStrokeScoreCard',
  () => import(/* webpackChunkName: "GolfStrokeScoreCard" */ '@/components/SharedViews/Card/ScoreCard/GolfScorecard/GolfStrokeScoreCard.vue'),
) as typeof import('@/components/SharedViews/Card/ScoreCard/GolfScorecard/GolfStrokeScoreCard.vue').default;

const GolfMatchPlayScoreCard = Vue.component<any>(
  'GolfMatchPlayScoreCard',
  () => import(/* webpackChunkName: "GolfMatchPlayScoreCard" */ '@/components/SharedViews/Card/ScoreCard/GolfScorecard/GolfMatchPlayScoreCard.vue'),
) as typeof import('@/components/SharedViews/Card/ScoreCard/GolfScorecard/GolfMatchPlayScoreCard.vue').default;

const TennisScoreCard = Vue.component<any>(
  'TennisScoreCard',
  () => import(/* webpackChunkName: "TennisScoreCard" */ '@/components/SharedViews/Card/ScoreCard/TennisScoreCard/TennisScoreCard.vue'),
) as typeof import('@/components/SharedViews/Card/ScoreCard/TennisScoreCard/TennisScoreCard.vue').default;

// VUE JSX HOT LOADER //
if (module.hot) require("/github/workspace/node_modules/vue-jsx-hot-loader/src/api.js")({ Vue: require('vue'), ctx: eval('this'), module: module, hotId: "_vue_jsx_hot-01341240/SummaryFetcher.vue" });